import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

const DIMENSIONS = [
  ['coordination', '协调性'],
  ['lower_body_stability', '下肢稳定性'],
  ['trunk_stability', '躯干稳定性'],
  ['upper_body_stability', '上肢稳定性'],
  ['upper_body_mobility', '上肢活动度'],
  ['lower_body_mobility', '下肢活动度'],
  ['trunk_mobility', '躯干活动度'],
  ['lower_body_strength', '下肢力量'],
  ['upper_body_strength', '上肢力量'],
  ['cardiovascular_endurance', '心肺耐力'],
].map(([value, label]) => ({ value, label }));

const RELATION_ROLES = [
  ['primary', '主要贡献'],
  ['strong_auxiliary', '强辅助贡献'],
  ['auxiliary', '辅助贡献'],
].map(([value, label]) => ({ value, label }));

const METRIC_OPTIONS = [
  ['validUnitCount', '有效动作单元数'],
  ['jointRom', '关节活动范围'],
  ['minAngle', '最小关节角度'],
  ['maxAngle', '最大关节角度'],
  ['eccentricDuration', '离心时长'],
  ['concentricDuration', '向心时长'],
  ['cycleDurationCv', '周期时长变异'],
  ['bodyCenterSwayX', '重心横向摆动'],
  ['bodyCenterSwayY', '重心纵向摆动'],
  ['trunkTiltStd', '躯干倾角标准差'],
  ['trunkTiltRange', '躯干倾角范围'],
].map(([value, label]) => ({ value, label }));

function parse(value) {
  try {
    return JSON.parse(value || '{}');
  } catch (error) {
    return {};
  }
}

function profileValues(scopeKey, source) {
  return {
    compatibleReleaseIds: (source.compatibleMotionConfigReleaseIds || []).join(
      ', ',
    ),
    relationships: (source.relationships || []).map((item) => ({
      dimension: item.dimension,
      relationRole: item.relationRole || 'auxiliary',
      relationCoefficient: item.relationCoefficient ?? 1,
      coverageContribution: (item.coverageContributions || []).join(', '),
      evidenceRecipeRef: item.evidenceRecipeRef || '',
    })),
    actionId: source.actionId || Number(scopeKey),
  };
}

function recipeValues(scopeKey, source) {
  const weights = source.position?.weights || {};
  return {
    recipeId: source.recipeId || scopeKey,
    dimension: source.dimension || 'lower_body_strength',
    measurements: (source.measurements || []).map((item) => ({
      measurementCode: item.measurementCode || '',
      metricCode: item.source?.metricCode || '',
      scope: item.source?.scope || 'whole_action',
      featureRef: item.source?.featureRef || '',
      weight: weights[item.measurementCode] ?? 1,
    })),
  };
}

function splitText(value) {
  return String(value || '')
    .split(/[,\n，]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

/** 将动作贡献和证据字段转为可选区块，避免日常配置直接书写 JSON。 */
export default function CapabilityConfigVisualBuilder({
  configType,
  scopeKey,
  value,
  onChange,
}) {
  const [form] = Form.useForm();
  const lastSynced = useRef();
  const isProfile = configType === 'action_profile';

  useEffect(() => {
    if (value === lastSynced.current) return;
    const source = parse(value);
    form.setFieldsValue(
      isProfile
        ? profileValues(scopeKey, source)
        : recipeValues(scopeKey, source),
    );
    lastSynced.current = value;
  }, [configType, form, isProfile, scopeKey, value]);

  const emit = () => {
    const fields = form.getFieldsValue(true);
    const content = isProfile
      ? {
          schemaVersion: 'action-capability-profile@1',
          actionId: Number(fields.actionId || scopeKey),
          compatibleMotionConfigReleaseIds: splitText(
            fields.compatibleReleaseIds,
          ),
          relationships: (fields.relationships || []).map((item) => ({
            dimension: item.dimension,
            relationRole: item.relationRole,
            relationCoefficient: Number(item.relationCoefficient || 0),
            coverageContributions: splitText(item.coverageContribution),
            evidenceRecipeRef: item.evidenceRecipeRef,
          })),
        }
      : {
          schemaVersion: 'capability-evidence-recipe@1',
          recipeId: fields.recipeId || scopeKey,
          dimension: fields.dimension,
          measurements: (fields.measurements || []).map((item) => ({
            measurementCode: item.measurementCode,
            source: {
              metricCode: item.metricCode,
              scope: item.scope,
              ...(item.featureRef ? { featureRef: item.featureRef } : {}),
            },
            transform: 'identity@1',
          })),
          position: {
            method: 'factual_measurement_set@1',
            weights: (fields.measurements || []).reduce((result, item) => {
              if (item.measurementCode) {
                result[item.measurementCode] = Number(item.weight || 1);
              }
              return result;
            }, {}),
          },
        };
    const next = JSON.stringify(content, null, 2);
    lastSynced.current = next;
    onChange(next);
  };

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message={
          isProfile ? '选择动作贡献的能力维度' : '选择要引用的客观证据字段'
        }
        description={
          isProfile
            ? '每一行表示该动作与一个能力维度的客观关联。'
            : '每一行表示一个来自动作分析结果的客观字段，不包含好坏判断。'
        }
      />
      <Form form={form} layout="vertical" onValuesChange={emit}>
        {isProfile ? (
          <>
            <Form.Item name="actionId" label="动作 ID">
              <InputNumber disabled style={{ width: 220 }} />
            </Form.Item>
            <Form.Item
              name="compatibleReleaseIds"
              label="适用的动作配置 Release"
              extra="填写当前动作配置的 Release ID；多个版本用逗号分隔。"
              rules={[{ required: true, message: '请填写动作配置 Release ID' }]}
            >
              <Input placeholder="例如 rel_4049_initial_7da824f8" />
            </Form.Item>
            <Form.List name="relationships">
              {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {fields.map((field) => (
                    <Card
                      key={field.key}
                      size="small"
                      title={`能力关联 ${field.name + 1}`}
                      extra={
                        <MinusCircleOutlined
                          onClick={() => remove(field.name)}
                        />
                      }
                    >
                      <Space wrap align="start">
                        <Form.Item
                          {...field}
                          name={[field.name, 'dimension']}
                          label="能力维度"
                          rules={[{ required: true }]}
                        >
                          <Select style={{ width: 180 }} options={DIMENSIONS} />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'relationRole']}
                          label="贡献角色"
                          rules={[{ required: true }]}
                        >
                          <Select
                            style={{ width: 150 }}
                            options={RELATION_ROLES}
                          />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'relationCoefficient']}
                          label="关联系数"
                          rules={[{ required: true }]}
                        >
                          <InputNumber
                            min={0}
                            max={1}
                            step={0.1}
                            style={{ width: 120 }}
                          />
                        </Form.Item>
                      </Space>
                      <Form.Item
                        {...field}
                        name={[field.name, 'evidenceRecipeRef']}
                        label="证据字段配置"
                        rules={[{ required: true }]}
                      >
                        <Input placeholder="例如 squat_lower_body_strength@1" />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, 'coverageContribution']}
                        label="覆盖标签"
                      >
                        <Input placeholder="多个标签用逗号分隔" />
                      </Form.Item>
                    </Card>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      add({ relationRole: 'auxiliary', relationCoefficient: 1 })
                    }
                  >
                    添加能力关联
                  </Button>
                </Space>
              )}
            </Form.List>
          </>
        ) : (
          <>
            <Space wrap align="start">
              <Form.Item
                name="recipeId"
                label="证据字段配置 ID"
                rules={[{ required: true }]}
              >
                <Input style={{ width: 280 }} />
              </Form.Item>
              <Form.Item
                name="dimension"
                label="能力维度"
                rules={[{ required: true }]}
              >
                <Select style={{ width: 180 }} options={DIMENSIONS} />
              </Form.Item>
            </Space>
            <Form.List name="measurements">
              {(fields, { add, remove }) => (
                <Space direction="vertical" style={{ width: '100%' }}>
                  {fields.map((field) => (
                    <Card
                      key={field.key}
                      size="small"
                      title={`证据字段 ${field.name + 1}`}
                      extra={
                        <MinusCircleOutlined
                          onClick={() => remove(field.name)}
                        />
                      }
                    >
                      <Space wrap align="start">
                        <Form.Item
                          {...field}
                          name={[field.name, 'measurementCode']}
                          label="字段名称"
                          rules={[{ required: true }]}
                        >
                          <Input
                            placeholder="例如 knee_joint_rom"
                            style={{ width: 180 }}
                          />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'metricCode']}
                          label="动作分析指标"
                          rules={[{ required: true }]}
                        >
                          <Select
                            showSearch
                            optionFilterProp="label"
                            style={{ width: 200 }}
                            options={METRIC_OPTIONS}
                          />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'scope']}
                          label="汇总范围"
                          rules={[{ required: true }]}
                        >
                          <Select
                            style={{ width: 140 }}
                            options={[
                              { value: 'summary', label: '动作汇总' },
                              { value: 'whole_action', label: '完整动作' },
                            ]}
                          />
                        </Form.Item>
                      </Space>
                      <Form.Item
                        {...field}
                        name={[field.name, 'featureRef']}
                        label="特征引用（可选）"
                      >
                        <Input placeholder="例如 knee_joint_rom" />
                      </Form.Item>
                      <Form.Item hidden name={[field.name, 'weight']}>
                        <InputNumber />
                      </Form.Item>
                    </Card>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ scope: 'whole_action', weight: 1 })}
                  >
                    添加证据字段
                  </Button>
                </Space>
              )}
            </Form.List>
          </>
        )}
      </Form>
    </Space>
  );
}

CapabilityConfigVisualBuilder.propTypes = {
  configType: PropTypes.oneOf(['action_profile', 'evidence_recipe']).isRequired,
  scopeKey: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

CapabilityConfigVisualBuilder.defaultProps = {
  value: '',
};
